"use client";

import * as React from "react";
import {
  Text,
  SectionCard,
  CardTitle,
  CardDescription,
  Button,
  Switch,
  Label,
  Badge,
  Spinner,
  Skeleton,
  SkeletonText,
  SkeletonAvatar,
  SkeletonCard,
  SkeletonTable,
  SkeletonList,
  LoadingState,
  LoadingOverlay,
  InlineLoading,
  ErrorState,
  EmptyState,
  Section,
} from "@/src/components";
import { SelectBlock } from "@/src/components/input/select-advanced";

import { Inbox, Search, FileX, Plus } from "lucide-react";
import { InstallCommand } from "@/components/docs/install-command";

const sizeOptions = [
  { label: "Small", value: "sm" },
  { label: "Medium", value: "md" },
  { label: "Large", value: "lg" },
  { label: "Extra Large", value: "xl" },
];

export default function DocsComponentsStatePatterns() {
  const [spinnerSize, setSpinnerSize] = React.useState<"sm" | "md" | "lg" | "xl">("md");
  const [isOverlayLoading, setIsOverlayLoading] = React.useState(false);

  return (
    <>
      {/* Header */}
      <Section>
        <div className="space-y-4">
          <Text as="h1" className="text-h2 font-bold">
            State Patterns
          </Text>
          <Text className="text-lg text-muted-foreground max-w-2xl">
            Components for handling loading, error, and empty states consistently
            across your application.
          </Text>
        </div>
      </Section>

      {/* Installation */}
      <Section>
        <SectionCard
          header={
            <>
              <CardTitle>Installation</CardTitle>
              <CardDescription>Add this component to your project</CardDescription>
            </>
          }
          contentClassName="p-6"
        >
          <InstallCommand command="npx shadcn@latest add @bluecopa-ui/state-patterns" />
        </SectionCard>
      </Section>

      {/* Overview */}
      <Section>
        <SectionCard
        header={<CardTitle>Overview</CardTitle>}
        contentClassName="p-6"
      >
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="space-y-2">
            <Badge variant="secondary">Loading</Badge>
            <Text className="text-sm text-muted-foreground">
              Spinners, skeletons, and loading overlays for async operations.
            </Text>
          </div>
          <div className="space-y-2">
            <Badge variant="secondary">Error</Badge>
            <Text className="text-sm text-muted-foreground">
              Error boundaries, error states, and retry patterns.
            </Text>
          </div>
          <div className="space-y-2">
            <Badge variant="secondary">Empty</Badge>
            <Text className="text-sm text-muted-foreground">
              Empty states for lists, tables, and search results.
            </Text>
          </div>
        </div>
      </SectionCard>
      </Section>

      {/* Spinner */}
      <Section>
        <SectionCard
        header={
          <>
            <CardTitle>Spinner</CardTitle>
            <CardDescription>Loading indicator for async operations</CardDescription>
          </>
        }
        contentClassName="p-6"
      >
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Preview */}
          <div className="flex items-center justify-center min-h-32 bg-muted/30 rounded-lg border-2 border-dashed">
            <Spinner size={spinnerSize} />
          </div>

          {/* Controls */}
          <div className="space-y-4">
            <SelectBlock
              label="Size"
              value={sizeOptions.find((o) => o.value === spinnerSize) || null}
              onChange={(v) => setSpinnerSize((v as typeof sizeOptions[0])?.value as typeof spinnerSize)}
              options={sizeOptions}
            />
          </div>
        </div>

        <div className="mt-6 flex flex-wrap gap-6">
          <div className="text-center">
            <Spinner size="sm" />
            <Text className="text-xs text-muted-foreground mt-2">sm</Text>
          </div>
          <div className="text-center">
            <Spinner size="md" />
            <Text className="text-xs text-muted-foreground mt-2">md</Text>
          </div>
          <div className="text-center">
            <Spinner size="lg" />
            <Text className="text-xs text-muted-foreground mt-2">lg</Text>
          </div>
          <div className="text-center">
            <Spinner size="xl" />
            <Text className="text-xs text-muted-foreground mt-2">xl</Text>
          </div>
        </div>
      </SectionCard>
      </Section>

      {/* Loading State */}
      <Section>
        <SectionCard
        header={
          <>
            <CardTitle>LoadingState</CardTitle>
            <CardDescription>Centered loading indicator with optional message</CardDescription>
          </>
        }
        contentClassName="p-6 space-y-6"
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="border rounded-lg">
            <LoadingState message="Loading content..." />
          </div>
          <div className="border rounded-lg">
            <LoadingState message="Please wait..." orientation="horizontal" />
          </div>
        </div>

        <div className="bg-muted/50 rounded-lg p-4 font-mono text-sm">
          <code>{'<LoadingState message="Loading content..." />'}</code>
        </div>
      </SectionCard>
      </Section>

      {/* Loading Overlay */}
      <Section>
        <SectionCard
        header={
          <>
            <CardTitle>LoadingOverlay</CardTitle>
            <CardDescription>Loading indicator over existing content</CardDescription>
          </>
        }
        contentClassName="p-6 space-y-6"
      >
        <div className="flex items-center gap-3 mb-4">
          <Switch
            id="overlay-loading"
            checked={isOverlayLoading}
            onCheckedChange={setIsOverlayLoading}
          />
          <Label htmlFor="overlay-loading">Toggle loading</Label>
        </div>

        <LoadingOverlay isLoading={isOverlayLoading} message="Updating...">
          <div className="p-6 border rounded-lg space-y-4">
            <Text className="font-semibold">Card Content</Text>
            <Text className="text-sm text-muted-foreground">
              This content remains visible but blurred while loading.
            </Text>
            <Button size="sm">Action</Button>
          </div>
        </LoadingOverlay>
      </SectionCard>
      </Section>

      {/* Inline Loading */}
      <Section>
        <SectionCard
        header={
          <>
            <CardTitle>InlineLoading</CardTitle>
            <CardDescription>Small inline loading for buttons and status</CardDescription>
          </>
        }
        contentClassName="p-6 space-y-4"
      >
        <div className="flex flex-wrap gap-4">
          <Button disabled>
            <InlineLoading message="Saving..." />
          </Button>
          <div className="flex items-center gap-2 text-sm">
            Status: <InlineLoading message="Syncing" />
          </div>
        </div>
      </SectionCard>
      </Section>

      {/* Skeleton Variants */}
      <Section>
        <SectionCard
        header={
          <>
            <CardTitle>Skeleton Variants</CardTitle>
            <CardDescription>Placeholder shapes for content loading</CardDescription>
          </>
        }
        contentClassName="p-6 space-y-6"
      >
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          <div className="space-y-2">
            <Skeleton className="h-4 w-full" />
            <Text className="text-xs text-muted-foreground">Default</Text>
          </div>
          <div className="space-y-2">
            <Skeleton variant="text" />
            <Text className="text-xs text-muted-foreground">Text</Text>
          </div>
          <div className="space-y-2">
            <Skeleton variant="circular" width={48} height={48} />
            <Text className="text-xs text-muted-foreground">Circular</Text>
          </div>
          <div className="space-y-2">
            <Skeleton variant="rectangular" width="100%" height={64} />
            <Text className="text-xs text-muted-foreground">Rectangular</Text>
          </div>
        </div>
      </SectionCard>
      </Section>

      {/* Skeleton Composites */}
      <Section>
        <SectionCard
        header={
          <>
            <CardTitle>Skeleton Composites</CardTitle>
            <CardDescription>Pre-built skeleton layouts for common patterns</CardDescription>
          </>
        }
        contentClassName="p-6 space-y-8"
      >
        <div className="space-y-3">
          <Text className="font-medium">SkeletonText (3 lines)</Text>
          <SkeletonText lines={3} />
        </div>

        <div className="space-y-3">
          <Text className="font-medium">SkeletonAvatar (sizes)</Text>
          <div className="flex gap-4">
            <SkeletonAvatar size="sm" />
            <SkeletonAvatar size="md" />
            <SkeletonAvatar size="lg" />
            <SkeletonAvatar size="xl" />
          </div>
        </div>

        <div className="space-y-3">
          <Text className="font-medium">SkeletonCard</Text>
          <div className="max-w-sm">
            <SkeletonCard showHeader showFooter contentHeight={100} />
          </div>
        </div>

        <div className="space-y-3">
          <Text className="font-medium">SkeletonList</Text>
          <SkeletonList items={3} />
        </div>
      </SectionCard>
      </Section>

      {/* Skeleton Table */}
      <Section>
        <SectionCard
        header={
          <>
            <CardTitle>SkeletonTable</CardTitle>
            <CardDescription>Table loading state placeholder</CardDescription>
          </>
        }
        contentClassName="p-6"
      >
        <SkeletonTable rows={4} columns={4} showCheckbox showHeader />
      </SectionCard>
      </Section>

      {/* Error State */}
      <Section>
        <SectionCard
        header={
          <>
            <CardTitle>ErrorState</CardTitle>
            <CardDescription>Error display with retry option</CardDescription>
          </>
        }
        contentClassName="p-6"
      >
        <div className="border rounded-lg">
          <ErrorState
            title="Failed to load data"
            description="There was a problem loading your data. Please check your connection and try again."
            onRetry={() => alert("Retry clicked!")}
          />
        </div>
      </SectionCard>
      </Section>

      {/* Empty States */}
      <Section>
        <SectionCard
        header={
          <>
            <CardTitle>EmptyState</CardTitle>
            <CardDescription>Display when there's no data</CardDescription>
          </>
        }
        contentClassName="p-6 space-y-6"
      >
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* No data */}
          <div className="border rounded-lg">
            <EmptyState
              icon={<Inbox className="h-10 w-10" />}
              heading="No items yet"
              description="Get started by creating your first item"
              actions={<Button size="sm"><Plus className="mr-2 h-4 w-4" /> Create</Button>}
            />
          </div>

          {/* No search results */}
          <div className="border rounded-lg">
            <EmptyState
              icon={<Search className="h-10 w-10" />}
              heading="No results found"
              description="Try adjusting your search or filters"
              actions={<Button variant="outline" size="sm">Clear Filters</Button>}
            />
          </div>

          {/* Error state */}
          <div className="border rounded-lg">
            <EmptyState
              icon={<FileX className="h-10 w-10" />}
              heading="Something went wrong"
              description="We couldn't load this content"
              actions={<Button variant="outline" size="sm">Retry</Button>}
            />
          </div>
        </div>
      </SectionCard>
      </Section>

      {/* Usage Patterns */}
      <Section>
        <SectionCard
        header={<CardTitle>Usage Patterns</CardTitle>}
        contentClassName="p-6 space-y-6"
      >
        <div className="space-y-3">
          <Text className="font-semibold">Data Fetching Pattern:</Text>
          <div className="bg-muted/50 rounded-lg p-4 font-mono text-sm overflow-x-auto space-y-1">
            <code className="block">{"function DataList() {"}</code>
            <code className="block pl-4">{"const { data, isLoading, error } = useQuery(...);"}</code>
            <code className="block mt-2 pl-4">{"if (isLoading) return <SkeletonList items={5} />;"}</code>
            <code className="block pl-4">{"if (error) return <ErrorState onRetry={refetch} />;"}</code>
            <code className="block pl-4">{"if (!data?.length) return <EmptyState ... />;"}</code>
            <code className="block mt-2 pl-4">{"return <List data={data} />;"}</code>
            <code className="block">{"}"}</code>
          </div>
        </div>

        <div className="space-y-3">
          <Text className="font-semibold">Button Loading State:</Text>
          <div className="bg-muted/50 rounded-lg p-4 font-mono text-sm overflow-x-auto space-y-1">
            <code className="block">{"<Button disabled={isSubmitting}>"}</code>
            <code className="block pl-4">{"{isSubmitting ? <InlineLoading message=\"Saving...\" /> : \"Save\"}"}</code>
            <code className="block">{"</Button>"}</code>
          </div>
        </div>
      </SectionCard>
      </Section>

      {/* Components Reference */}
      <Section>
        <SectionCard
        header={<CardTitle>Component Reference</CardTitle>}
        contentClassName="p-6"
      >
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b">
              <th className="text-left py-2 font-semibold">Component</th>
              <th className="text-left py-2 font-semibold">Use Case</th>
              <th className="text-left py-2 font-semibold">Import From</th>
            </tr>
          </thead>
          <tbody>
            <tr className="border-b">
              <td className="py-2 font-mono text-xs">Spinner</td>
              <td className="py-2 text-muted-foreground">Inline loading indicators</td>
              <td className="py-2 font-mono text-xs">@/ui/spinner</td>
            </tr>
            <tr className="border-b">
              <td className="py-2 font-mono text-xs">LoadingState</td>
              <td className="py-2 text-muted-foreground">Centered loading with message</td>
              <td className="py-2 font-mono text-xs">@/ui/loading-state</td>
            </tr>
            <tr className="border-b">
              <td className="py-2 font-mono text-xs">LoadingOverlay</td>
              <td className="py-2 text-muted-foreground">Loading over existing content</td>
              <td className="py-2 font-mono text-xs">@/ui/loading-state</td>
            </tr>
            <tr className="border-b">
              <td className="py-2 font-mono text-xs">Skeleton*</td>
              <td className="py-2 text-muted-foreground">Content placeholders</td>
              <td className="py-2 font-mono text-xs">@/ui/skeleton</td>
            </tr>
            <tr className="border-b">
              <td className="py-2 font-mono text-xs">ErrorBoundary</td>
              <td className="py-2 text-muted-foreground">Catch React errors</td>
              <td className="py-2 font-mono text-xs">@/ui/error-boundary</td>
            </tr>
            <tr className="border-b">
              <td className="py-2 font-mono text-xs">ErrorState</td>
              <td className="py-2 text-muted-foreground">Error display with retry</td>
              <td className="py-2 font-mono text-xs">@/ui/error-boundary</td>
            </tr>
            <tr className="border-b">
              <td className="py-2 font-mono text-xs">EmptyState</td>
              <td className="py-2 text-muted-foreground">No data messaging</td>
              <td className="py-2 font-mono text-xs">@/ui/empty-state</td>
            </tr>
          </tbody>
        </table>
      </SectionCard>
      </Section>
    </>
  );
}
